/**
 * Button Component
 * Reusable button with variants and sizes
 */

import { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
    size?: 'sm' | 'md' | 'lg';
    fullWidth?: boolean;
    loading?: boolean;
    icon?: React.ReactNode;
    iconPosition?: 'left' | 'right';
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            variant = 'primary',
            size = 'md',
            fullWidth = false,
            loading = false,
            icon,
            iconPosition = 'left',
            className = '',
            children,
            disabled,
            ...props
        },
        ref
    ) => {
        const variantClasses = {
            primary: 'bg-primary text-white hover:bg-primary-dark focus:ring-primary',
            secondary: 'bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-600',
            outline: 'border-2 border-primary text-primary hover:bg-primary hover:text-white focus:ring-primary',
            ghost: 'text-gray-600 hover:bg-gray-100 focus:ring-gray-400',
            danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-600',
        };

        const sizeClasses = {
            sm: 'px-3 py-1.5 text-sm',
            md: 'px-4 py-2.5 text-base',
            lg: 'px-6 py-3 text-lg',
        };

        const baseClasses = `
            inline-flex items-center justify-center gap-2 rounded-lg font-medium
            transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2
            disabled:opacity-50 disabled:cursor-not-allowed
            ${variantClasses[variant]}
            ${sizeClasses[size]}
            ${fullWidth ? 'w-full' : ''}
        `.trim();

        return (
            <button
                ref={ref}
                className={`${baseClasses} ${className}`}
                disabled={disabled || loading}
                {...props}
            >
                {loading ? (
                    <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Loading...</span>
                    </>
                ) : (
                    <>
                        {icon && iconPosition === 'left' && icon}
                        {children}
                        {icon && iconPosition === 'right' && icon}
                    </>
                )}
            </button>
        );
    }
);

Button.displayName = 'Button';

export default Button;
